import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabaseClient'
import { generateWords, getNextAlphabet } from '../lib/wordGenerator'
import { toast } from 'sonner'
import { Settings, Trophy, LogIn, LogOut, User, FileText, Shield, Info, HelpCircle } from 'lucide-react'
import { Link } from 'react-router-dom'
import Keyboard from '../components/Keyboard'
import TypirLogo from '../components/TypirLogo'

export default function Practice() {
  const { user, signInWithGoogle, signOut } = useAuth()
  const [currentAlphabet, setCurrentAlphabet] = useState('a')
  const [difficulty, setDifficulty] = useState('easy')
  const [duration, setDuration] = useState(60)
  const [generatedText, setGeneratedText] = useState('')
  const [userInput, setUserInput] = useState('')
  const [isActive, setIsActive] = useState(false)
  const [timeLeft, setTimeLeft] = useState(duration)
  const [wpm, setWpm] = useState(0)
  const [accuracy, setAccuracy] = useState(0)
  const [showSettings, setShowSettings] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [activeKey, setActiveKey] = useState('')
  const [keyStatus, setKeyStatus] = useState({})
  const [showResults, setShowResults] = useState(false)
  const inputRef = useRef(null)
  const [totalWordsTyped, setTotalWordsTyped] = useState(0)
  const [totalCorrectChars, setTotalCorrectChars] = useState(0)
  const [totalCharsTyped, setTotalCharsTyped] = useState(0)

  useEffect(() => {
    loadUserProgress()
  }, [user])

  useEffect(() => {
    const text = generateWords(currentAlphabet, difficulty, duration)
    setGeneratedText(text)
    setUserInput('')
    setTimeLeft(duration)
    setIsActive(false)
    setWpm(0)
    setAccuracy(0)
    setTotalWordsTyped(0)
    setTotalCorrectChars(0)
    setTotalCharsTyped(0)
  }, [currentAlphabet, difficulty, duration])

  useEffect(() => {
    if (isActive && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1)
      }, 1000)
      return () => clearInterval(timer)
    } else if (timeLeft === 0) {
      handleTestComplete()
    }
  }, [isActive, timeLeft])

  useEffect(() => {
    // Check if user completed all words and generate more
    if (userInput.length >= generatedText.length && timeLeft > 0 && isActive) {
      // Save current session stats to cumulative totals
      const wordsInSession = userInput.trim().split(/\s+/).length
      setTotalWordsTyped(prev => prev + wordsInSession)
      
      let correctChars = 0
      const minLength = Math.min(userInput.length, generatedText.length)
      for (let i = 0; i < minLength; i++) {
        if (userInput[i] === generatedText[i]) {
          correctChars++
        }
      }
      setTotalCorrectChars(prev => prev + correctChars)
      setTotalCharsTyped(prev => prev + minLength)
      
      // Replace with completely new words (not append)
      const newWords = generateWords(currentAlphabet, difficulty, 60)
      setGeneratedText(newWords)
      setUserInput('') // Reset user input
    }
  }, [userInput, generatedText, timeLeft, isActive, currentAlphabet, difficulty])

  useEffect(() => {
    // Focus on the component and listen for keyboard events
    const handleKeyDown = (e) => {
      if (timeLeft === 0) return
      
      // Ignore special keys except Backspace and Space
      if (e.key.length > 1 && e.key !== 'Backspace' && e.key !== ' ') return
      
      e.preventDefault()
      
      if (e.key === 'Backspace') {
        setUserInput(prev => prev.slice(0, -1))
      } else {
        if (userInput.length < generatedText.length) {
          const newInput = userInput + e.key
          setUserInput(newInput)
          
          // Set active key only (no correct/incorrect status for keyboard)
          setActiveKey(e.key)
          
          setTimeout(() => {
            setActiveKey('')
          }, 200)
        }
      }
    }
    
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [userInput, generatedText, timeLeft])

  const loadUserProgress = async () => {
    if (!user) {
      setCurrentAlphabet('a')
      setDifficulty('easy')
      return
    }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('last_trained_alphabet, current_difficulty')
        .eq('id', user.id)
        .single()

      if (data && !error) {
        setCurrentAlphabet(data.last_trained_alphabet || 'a')
        setDifficulty(data.current_difficulty || 'easy')
      }
    } catch (err) {
      console.error('Error loading progress:', err)
    }
  }

  const calculateStats = () => {
    const timeElapsed = duration - timeLeft
    if (timeElapsed === 0) {
      setWpm(0)
      setAccuracy(0)
      return
    }

    // Calculate current session words and accuracy
    const currentWordsTyped = userInput.length > 0 ? userInput.trim().split(/\s+/).length : 0
    const totalWords = totalWordsTyped + currentWordsTyped
    
    // Calculate cumulative WPM
    const calculatedWpm = Math.round((totalWords / timeElapsed) * 60)
    setWpm(calculatedWpm)

    // Calculate cumulative accuracy
    let currentCorrectChars = 0
    const minLength = Math.min(userInput.length, generatedText.length)
    for (let i = 0; i < minLength; i++) {
      if (userInput[i] === generatedText[i]) {
        currentCorrectChars++
      }
    }
    
    const totalCorrect = totalCorrectChars + currentCorrectChars
    const totalChars = totalCharsTyped + minLength
    const calculatedAccuracy = totalChars > 0 ? Math.round((totalCorrect / totalChars) * 100) : 0
    setAccuracy(calculatedAccuracy)
  }

  const handleTestComplete = async () => {
    setIsActive(false)
    calculateStats()
    setShowResults(true)

    const finalWpm = wpm
    const finalAccuracy = accuracy

    if (user) {
      try {
        const { error } = await supabase
          .from('profiles')
          .update({
            last_trained_alphabet: currentAlphabet,
            current_difficulty: difficulty,
            words_per_minute: finalWpm,
            last_accuracy: finalAccuracy,
            updated_at: new Date().toISOString(),
          })
          .eq('id', user.id)

        if (error) throw error
      } catch (err) {
        console.error('Error saving progress:', err)
      }
    }
  }

  useEffect(() => {
    if (userInput.length > 0 && !isActive) {
      setIsActive(true)
    }
    calculateStats()
  }, [userInput])

  const handleRestart = () => {
    const text = generateWords(currentAlphabet, difficulty, duration)
    setGeneratedText(text)
    setUserInput('')
    setTimeLeft(duration)
    setIsActive(false)
    setWpm(0)
    setAccuracy(0)
    setShowResults(false)
    setTotalWordsTyped(0)
    setTotalCorrectChars(0)
    setTotalCharsTyped(0)
  }

  const handleNextAlphabet = () => {
    const next = getNextAlphabet(currentAlphabet)
    setCurrentAlphabet(next)
    const text = generateWords(next, difficulty, duration)
    setGeneratedText(text)
    setUserInput('')
    setTimeLeft(duration)
    setIsActive(false)
    setWpm(0)
    setAccuracy(0)
    setShowResults(false)
    setTotalWordsTyped(0)
    setTotalCorrectChars(0)
    setTotalCharsTyped(0)
  }

  const handleSignIn = async () => {
    const { error } = await signInWithGoogle()
    if (error) {
      toast.error('Failed to sign in: ' + error.message)
    }
  }

  const handleSignOut = async () => {
    const { error } = await signOut()
    if (error) {
      toast.error('Failed to sign out')
    } else {
      toast.success('Signed out successfully')
      setCurrentAlphabet('a')
      setDifficulty('easy')
      handleRestart()
    }
  }

  const renderText = () => {
    // Render character by character, including spaces
    return generatedText.split('').map((char, index) => {
      let className = 'char'
      
      if (index < userInput.length) {
        // Character has been typed
        className += userInput[index] === char ? ' correct' : ' incorrect'
      } else if (index === userInput.length) {
        // Current character to type
        className += ' active'
      }
      
      // For spaces, use a visible space character
      return (
        <span key={index} className={className}>
          {char === ' ' ? '\u00A0' : char}
        </span>
      )
    })
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="practice-page" style={{ minHeight: '100vh', background: '#000000' }}>
      {/* Header */}
      <nav className="px-6 py-4 border-b border-gray-800 flex justify-between items-center">
        <div className="flex items-center gap-6">
          <TypirLogo size={40} />
        </div>
        
        <div className="flex items-center gap-6 text-sm">
          <Link to="/" className="text-gray-400 hover:text-white transition-colors">Practice</Link>
          {user && (
            <Link to="/dashboard" className="text-gray-400 hover:text-white transition-colors flex items-center gap-1">
              <User className="w-4 h-4" />
              Profile
            </Link>
          )}
          <Link to="/typing-test" className="text-gray-400 hover:text-white transition-colors">Typing Test</Link>
          <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors flex items-center gap-1">
            <Shield className="w-4 h-4" />
            Privacy
          </Link>
          <Link to="/about" className="text-gray-400 hover:text-white transition-colors flex items-center gap-1">
            <Info className="w-4 h-4" />
            About
          </Link>
          <Link to="/disclaimer" className="text-gray-400 hover:text-white transition-colors flex items-center gap-1">
            <FileText className="w-4 h-4" />
            Disclaimer
          </Link>
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <Settings className="w-5 h-5" />
          </button>
          {user ? (
            <button
              onClick={handleSignOut}
              className="text-gray-400 hover:text-white transition-colors flex items-center gap-2 px-4 py-1.5 border border-gray-700 rounded-lg hover:border-gray-500"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          ) : (
            <button
              onClick={handleSignIn}
              className="text-white bg-green-600 hover:bg-green-700 transition-colors flex items-center gap-2 px-4 py-1.5 rounded-lg"
            >
              <LogIn className="w-4 h-4" />
              Sign In
            </button>
          )}
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-2" style={{ height: 'calc(100vh - 65px)', display: 'flex', flexDirection: 'column' }}>
        {/* Results Modal */}
        {showResults && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-gray-900 border-2 border-green-500 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
              <h2 className="text-3xl font-bold text-white mb-6 text-center">Test Complete!</h2>
              <div className="space-y-4 mb-8">
                <div className="bg-black rounded-lg p-4 border border-gray-800">
                  <p className="text-gray-400 text-sm mb-1">Words Per Minute</p>
                  <p className="text-5xl font-bold text-green-500">{wpm}</p>
                </div>
                <div className="bg-black rounded-lg p-4 border border-gray-800">
                  <p className="text-gray-400 text-sm mb-1">Accuracy</p>
                  <p className="text-5xl font-bold text-green-500">{accuracy}%</p>
                </div>
              </div>
              <div className="space-y-3">
                <button 
                  onClick={handleNextAlphabet}
                  className="w-full btn-primary py-3 text-lg flex items-center justify-center gap-2"
                >
                  Start Next Alphabet ({getNextAlphabet(currentAlphabet).toUpperCase()})
                  <span className="text-xl">→</span>
                </button>
                <button 
                  onClick={handleRestart}
                  className="w-full btn-secondary py-3 text-lg"
                >
                  Restart ({currentAlphabet.toUpperCase()})
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Settings Panel */}
        {showSettings && (
          <div className="stat-card rounded-lg p-4 mb-3 border">
            <h3 className="text-lg font-bold mb-3 text-white">Settings</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">
                  Alphabet
                </label>
                <select
                  value={currentAlphabet}
                  onChange={(e) => setCurrentAlphabet(e.target.value)}
                  className="w-full px-3 py-1.5 text-sm bg-black border-2 border-gray-800 text-white rounded-lg focus:outline-none focus:border-green-500"
                  data-testid="alphabet-select"
                >
                  {'abcdefghijklmnopqrstuvwxyz'.split('').map((letter) => (
                    <option key={letter} value={letter}>
                      {letter.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">
                  Difficulty
                </label>
                <select
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value)}
                  className="w-full px-3 py-1.5 text-sm bg-black border-2 border-gray-800 text-white rounded-lg focus:outline-none focus:border-green-500"
                  data-testid="difficulty-select"
                >
                  <option value="easy">Lesson 1 - Easy</option>
                  <option value="medium">Lesson 2 - Medium</option>
                  <option value="hard">Lesson 3 - Hard</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">
                  Duration
                </label>
                <select
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                  className="w-full px-3 py-1.5 text-sm bg-black border-2 border-gray-800 text-white rounded-lg focus:outline-none focus:border-green-500"
                  data-testid="duration-select"
                >
                  <option value={60}>1 minute</option>
                  <option value={180}>3 minutes</option>
                  <option value={300}>5 minutes</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Stats Bar */}
        <div className="grid grid-cols-4 gap-3 mb-3">
          <div className="stat-card rounded-lg p-3 text-center">
            <p className="text-xs text-gray-400 mb-1">Time</p>
            <p className="text-2xl font-bold text-white" data-testid="time-left">{timeLeft}s</p>
          </div>
          <div className="stat-card rounded-lg p-3 text-center">
            <p className="text-xs text-gray-400 mb-1">WPM</p>
            <p className="text-2xl font-bold text-green-500" data-testid="wpm-display">{wpm}</p>
          </div>
          <div className="stat-card rounded-lg p-3 text-center">
            <p className="text-xs text-gray-400 mb-1">Accuracy</p>
            <p className="text-2xl font-bold text-green-500" data-testid="accuracy-display">{accuracy}%</p>
          </div>
          <div className="stat-card rounded-lg p-3 text-center">
            <p className="text-xs text-gray-400 mb-1">Letter</p>
            <p className="text-2xl font-bold text-white">{currentAlphabet.toUpperCase()}</p>
          </div>
        </div>

        {/* Typing Area */}
        <div 
          className="stat-card rounded-xl p-6 mb-3 border flex-shrink-0 cursor-text" 
          tabIndex={0}
          style={{ outline: 'none' }}
        >
          <div 
            className="typing-text p-4 bg-black rounded-lg overflow-y-auto" 
            style={{ 
              height: showSettings ? '180px' : '200px',
              minHeight: '180px',
              maxHeight: '200px'
            }}
          >
            {renderText()}
          </div>
          <div className="mt-4 text-center text-sm text-gray-500">
            {timeLeft > 0 ? 'Start typing to begin...' : 'Test complete!'}
          </div>
        </div>

        {/* Keyboard */}
        <div className="flex-shrink-0">
          <Keyboard activeKey={activeKey} keyStatus={keyStatus} />
        </div>

        {/* Actions */}
        <div className="flex justify-center gap-4 mt-3">
          <button onClick={handleRestart} className="btn-secondary px-6 py-2 text-sm" data-testid="restart-btn">
            Restart
          </button>
        </div>
      </div>
    </div>
  )
}
